import { Link } from 'react-router-dom';
import { Bookmark } from 'lucide-react';
import { useDynamicTranslate } from '@/hooks/useDynamicTranslate';
import { useTranslation } from 'react-i18next';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HR_MONTHS = ['SIJEČNJA','VELJAČE','OŽUJKA','TRAVNJA','SVIBNJA','LIPNJA','SRPNJA','KOLOVOZA','RUJNA','LISTOPADA','STUDENOGA','PROSINCA'];

// Inline SVG fallback when an article cover fails to load (broken hotlink,
// origin 4xx, etc.). Keeps the layout intact instead of leaving an empty
// gray box, and stops the browser from re-trying a broken URL forever.
const IMG_FALLBACK = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 10'><rect width='16' height='10' fill='%23e5e7eb'/><text x='8' y='6' text-anchor='middle' font-family='sans-serif' font-size='1.4' fill='%239ca3af'>e-mina.eu</text></svg>";

function handleImgError(e) {
  const img = e?.currentTarget;
  if (!img || img.dataset.fallback === '1') return;
  img.dataset.fallback = '1';
  img.src = IMG_FALLBACK;
  img.classList.add('opacity-60');
}

function formatDateHR(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getDate()}. ${HR_MONTHS[d.getMonth()]} ${d.getFullYear()}.`;
}

function CatBadge({ category }) {
  const name = useDynamicTranslate(category?.name_hr || '');
  if (!category) return null;
  const color = category.color || '#3b82f6';
  return (
    <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded" style={{ backgroundColor: color }}>
      {name || category.name_hr}
    </span>
  );
}

// SPONZORIRANO badge for paid placement in related/sidebar widgets.
// Visual: amber pill with a left-edge accent bar so it reads as "ad-like"
// at a glance without being garish. Falls back to the editor-supplied
// label ("Promocija", advertiser name, ...) when set.
function SponsoredBadge({ label }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.12em] text-amber-900 dark:text-amber-300 bg-amber-300/90 dark:bg-amber-500/15 border border-amber-500/60 px-1.5 py-0.5 rounded"
      data-testid="sponsored-badge"
      title="Sponzorirani sadržaj — plaćena objava"
    >
      <span className="inline-block w-[6px] h-[6px] rounded-full bg-amber-600 dark:bg-amber-400" />
      {(label || 'Sponzorirano').toUpperCase()}
    </span>
  );
}

// Returns true when a sponsored flag is currently active (no expiry, or
// expiry is in the future). Avoids a stale "SPONZORIRANO" badge on
// articles whose paid window has elapsed.
function isSponsoredActive(article) {
  if (!article?.is_sponsored) return false;
  const until = article.sponsored_until;
  if (!until) return true;
  try {
    return new Date(until).getTime() > Date.now();
  } catch {
    return true;
  }
}

export default function ArticleCard({ article, size = 'default' }) {
  const { i18n } = useTranslation();
  // Prefer server-resolved translation (when ?lang= was passed to the API);
  // fall back to on-the-fly dynamic translation driven by the active UI
  // language. This keeps HR free of overhead while giving non-HR readers
  // translated titles/excerpts everywhere ArticleCard is rendered.
  const titleResolved = article.title_translations?.[i18n.language] || article.title_hr || '';
  const excerptResolved = article.excerpt_translations?.[i18n.language] || article.excerpt_hr || '';
  const title = useDynamicTranslate(titleResolved);
  const excerpt = useDynamicTranslate(excerptResolved);

  const coverUrl = article.cover_image_url?.startsWith('http') ? article.cover_image_url : `${BACKEND}${article.cover_image_url}`;
  const date = formatDateHR(article.publish_at);
  const showRedFallback = !article.cover_image_url && article.is_breaking;
  const sponsoredOn = isSponsoredActive(article);
  const badgeRow = (
    <div className="flex items-center gap-1.5 flex-wrap">
      <CatBadge category={article.category} />
      {sponsoredOn && <SponsoredBadge label={article.sponsored_label} />}
    </div>
  );

  if (size === 'hero') {
    return (
      <Link to={`/clanak/${article.slug}`} className="block relative group overflow-hidden rounded-lg" data-testid="hero-article">
        <div className="aspect-[16/10] md:aspect-[4/3] relative">
          {showRedFallback ? (
            <div className="w-full h-full bg-red-600 opacity-50" data-testid="breaking-red-fallback" />
          ) : (
            <img src={coverUrl} alt={title} loading="eager" fetchpriority="high" decoding="async" referrerPolicy="no-referrer" onError={handleImgError} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          )}
          {article.is_breaking && !showRedFallback && (
            <div className="absolute inset-0 bg-red-600 opacity-50 pointer-events-none" data-testid="breaking-red-overlay" />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        {article.is_breaking && (
          <span className="absolute top-3 right-3 inline-block bg-red-600 text-white text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded shadow animate-pulse z-10">BREAKING</span>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
          {badgeRow}
          <h2 className="text-lg md:text-2xl font-bold text-white leading-tight mt-2 group-hover:text-blue-200 transition-colors">{title}</h2>
          <span className="text-[11px] text-white/60 mt-2 block uppercase tracking-wider">{date}</span>
        </div>
      </Link>
    );
  }

  if (size === 'sidebar') {
    return (
      <Link to={`/clanak/${article.slug}`} className="flex gap-3 group py-2.5 border-b border-gray-100 dark:border-gray-800 last:border-0" data-testid={`sidebar-card-${article.slug}`}>
        {article.cover_image_url ? (
          <div className="w-20 h-14 rounded overflow-hidden shrink-0 bg-gray-200">
            <img src={coverUrl} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={handleImgError} className="w-full h-full object-cover" />
          </div>
        ) : showRedFallback ? (
          <div className="w-20 h-14 rounded overflow-hidden shrink-0 bg-red-600 opacity-50" data-testid="breaking-red-fallback" />
        ) : null}
        <div className="min-w-0 flex-1">
          {badgeRow}
          <h4 className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 group-hover:text-red-600 transition-colors line-clamp-2 leading-snug mt-1">{title}</h4>
          <span className="text-[10px] text-gray-400 mt-1 block uppercase tracking-wider">{date}</span>
        </div>
      </Link>
    );
  }

  if (size === 'medium') {
    return (
      <Link to={`/clanak/${article.slug}`} className="group block relative" data-testid={`article-card-${article.slug}`}>
        {article.cover_image_url ? (
          <div className="aspect-[16/10] rounded-lg overflow-hidden mb-2.5 bg-gray-200 relative">
            <img src={coverUrl} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={handleImgError} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            {article.is_breaking && (
              <div className="absolute inset-0 bg-red-600 opacity-50 pointer-events-none" data-testid="breaking-red-overlay" />
            )}
            {article.is_breaking && (
              <span className="absolute top-2 left-2 inline-block bg-red-600 text-white text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded shadow animate-pulse z-10">BREAKING</span>
            )}
          </div>
        ) : showRedFallback ? (
          <div className="aspect-[16/10] rounded-lg overflow-hidden mb-2.5 bg-red-600 opacity-50 relative" data-testid="breaking-red-fallback" />
        ) : null}
        {badgeRow}
        <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 group-hover:text-red-600 transition-colors mt-1.5 leading-snug line-clamp-2">{title}</h3>
        <span className="text-[10px] text-gray-400 mt-1.5 block uppercase tracking-wider">{date}</span>
      </Link>
    );
  }

  // default
  return (
    <Link to={`/clanak/${article.slug}`} className="group block relative" data-testid={`article-card-${article.slug}`}>
      {article.cover_image_url ? (
        <div className="aspect-[16/10] rounded-lg overflow-hidden mb-3 bg-gray-200 relative">
          <img src={coverUrl} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={handleImgError} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          {article.is_breaking && (
            <div className="absolute inset-0 bg-red-600 opacity-50 pointer-events-none" data-testid="breaking-red-overlay" />
          )}
          {article.is_breaking && (
            <span className="absolute top-2 left-2 inline-block bg-red-600 text-white text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded shadow animate-pulse z-10">BREAKING</span>
          )}
        </div>
      ) : showRedFallback ? (
        <div className="aspect-[16/10] rounded-lg overflow-hidden mb-3 bg-red-600 opacity-50 relative" data-testid="breaking-red-fallback" />
      ) : null}
      {badgeRow}
      <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 group-hover:text-red-600 transition-colors mt-1.5 leading-snug line-clamp-2">{title}</h3>
      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{excerpt}</p>
      <span className="text-[10px] text-gray-400 mt-1.5 block uppercase tracking-wider">{date}</span>
    </Link>
  );
}
